import React, {useState, useEffect} from "react";
import {PageTitle} from "../../Components/Landmarks";
import {locations} from "../../Utilities/Location";
import {allow, policyEvents} from "../../Components/Authorize";
import {DashboardTile} from "./DashboardTile";
import {GridCell, gridConstants, GridContainer} from "../../Components/Grid";
import {LayoutFullBleed} from "../../Components/Layout";
import {EmailLink, ExternalLink} from "../../Components/Link";
import UserGuide from "../../docs/FimcUserGuide.pdf"
import {SelectField} from "../../Components/Inputs";
import {convertCamelCaseToSpaceDelimited} from "../../Utilities/commonUtilities";
import {SYSTEM_TYPES, SYSTEM_TYPES_LIST} from "../Library/libraryConstants";
import {ManageResourceVisibilityContainer} from "./ManageResourceVisibilityContainer";
import {iconTitles} from "../../Components/Tile/tileConstants";
import {useAppContext, useUserContext} from "../../Components/StateManager/Context";
import {userApi} from "../User/userApi";
import {USER_PREFERENCE} from "../../Components/StateManager/Context/userConstants";


export const DashboardContainer: React.FC = () => {
    const {user, userId, fetchUser} = useUserContext();
    const {handleApiCall} = useAppContext();

    const userPreferredSystemTypeId = (user as Record<string, unknown>).preferredSystemTypeId as number | undefined;
    const [systemType, setSystemType] = useState(userPreferredSystemTypeId || SYSTEM_TYPES.Textbooks);

    useEffect(() => {
        setSystemType(userPreferredSystemTypeId || SYSTEM_TYPES.Textbooks);
    }, [userPreferredSystemTypeId]);


    const canViewTextbooks = allow(policyEvents.VIEW_TEXTBOOKS);
    const canViewAph = allow(policyEvents.VIEW_APH);
    const canViewLoanLibrary = allow(policyEvents.VIEW_LOAN_LIBRARY);
    const canViewBrailler = allow(policyEvents.VIEW_BRAILLER);

    const canManageLoans = allow(policyEvents.MANAGE_LOANS);
    const canManageAphLoans = allow(policyEvents.MANAGE_APH_LOANS);
    const canManageOrders = allow(policyEvents.MANAGE_ORDER);
    const canManageResources = canManageLoans || canManageAphLoans || canManageOrders;

    const canAdminStudents = allow(policyEvents.VIEW_STUDENTS);
    const canAdminUsers = allow(policyEvents.APPROVE_USERS);
    const canAdminReports = allow(policyEvents.ADVANCE_SEARCH);
    const canAdministrate = canAdminStudents || canAdminUsers || canAdminReports;

    const noAccess = !canViewTextbooks && !canManageResources && !canAdministrate;

    const handleSystemTypeChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = Number(event.target.value);
        if (!Object.values(SYSTEM_TYPES).includes(newValue) || newValue === systemType) return;

        await handleApiCall(
            () => userApi.updateUserPreference(userId as string, USER_PREFERENCE.SystemTypeId, newValue.toString()),
            undefined,
            () => {
            }
        );

        setSystemType(newValue);

        fetchUser();
    }

    return (
        <>
            <div className={"dashboard-container"}>

                <GridContainer>
                    <GridCell medium_columns={gridConstants.column.ELEVEN}>
                        <PageTitle h1={"Dashboard"}/>
                    </GridCell>
                    <GridCell medium_columns={gridConstants.column.ONE}>
                        <ExternalLink label={"User Guide"} url={UserGuide} className={"-help-icon"}/>
                    </GridCell>

                    <GridCell>
                        <div>
                            Textbooks, APH products, and braillers may only be requested for students who are
                            in <b>Approved</b> status. if you need to request these materials for a student with
                            a <b>Pending</b> or <b>Need
                            Info</b> status, please email <EmailLink address="tconrad@fimcvi.org" className="-email"/>
                            with the FIMC-VI student ID number to request expedited processing.
                            <br/><br/>
                            Annual planned down times:
                            <ul>
                                <li>
                                    February 1-7: editing existing students or registering new students will be
                                    unavailable
                                </li>
                                <li>
                                    September 5-30: no APH orders may be submitted
                                </li>
                            </ul>
                        </div>
                    </GridCell>
                </GridContainer>
            </div>

            <LayoutFullBleed className={`catalog`}>
                {
                    noAccess &&
                    <p className="noAccess">Based on your current role, you do not have access to functions within
                        FIMC-VI&apos;s Portal</p>
                }
                {
                    <>
                        <header>
                            <h2>Resources</h2>
                        </header>
                        <GridContainer hasMarginX hasMarginY className={`dashboard`}>
                            {
                                canViewTextbooks &&
                                <DashboardTile
                                    {...locations.LIBRARY_TEXTBOOKS.link}
                                    description={"Search, view, update"}
                                    iconName="Textbooks"
                                />
                            }

                            {
                                canViewAph &&
                                <DashboardTile
                                    {...locations.APH_CATALOG.link}
                                    description={"Search, view, update"}
                                    iconName="APH"
                                    {...locations.APH_CATALOG.link}

                                />
                            }
                            {
                                canViewLoanLibrary &&
                                <DashboardTile
                                    {...locations.LIBRARY_LOAN.link}
                                    description={"Search, view, update"}
                                    iconName="Loan-Library"
                                />
                            }
                            {
                                canViewBrailler &&
                                <DashboardTile
                                    {...locations.LIBRARY_BRAILLERS.link}
                                    description={"Search, view, update"}
                                    iconName="Braille-Machines"
                                />
                            }


                        </GridContainer>
                        <br/>
                        <br/>
                    </>
                }
                {
                    canManageResources &&
                    <>
                        <header>
                            <h2>Requested Resources</h2>
                        </header>

                        <SelectField
                            label={convertCamelCaseToSpaceDelimited("SystemType", true)}
                            value={systemType.toString()}
                            onChange={handleSystemTypeChange}
                            name={"SystemType"}
                            includeDefaultOption={false}
                            options={SYSTEM_TYPES_LIST}
                        />

                        <GridContainer hasMarginX hasMarginY className={`dashboard`}>
                            <ManageResourceVisibilityContainer
                                systemType={systemType}
                                canManageLoans={canManageLoans}
                                canManageOrders={canManageOrders}
                                canManageAphLoans={canManageAphLoans}
                            >
                                <DashboardTile
                                    {...(systemType.toString() === SYSTEM_TYPES.APH.toString()
                                            ? locations.APH_LOAN_MANAGEMENT.getLink(systemType)
                                            : systemType.toString() === SYSTEM_TYPES.LoanLibrary.toString()
                                                ? locations.LOAN_LIBRARY_LOAN_MANAGEMENT.getLink()
                                                : systemType.toString() === SYSTEM_TYPES.Braillers.toString()
                                                    ? locations.BRAILLER_LOAN_MANAGEMENT.getLink()
                                                    : locations.LOAN_MANAGEMENT.getLink(systemType)
                                    )}
                                    description={"Search, view, update"}
                                    iconName={iconTitles.LOAN_MANAGEMENT}
                                />
                                <DashboardTile
                                    {...locations.ORDER_MANAGEMENT.link}
                                    description={"Manage orders and special orders"}
                                    iconName={iconTitles.ORDER_MANAGEMENT}
                                />
                                <DashboardTile
                                    {...locations.CHECK_IN.getLink(systemType)}
                                    description={"Check in resources"}
                                    iconName={iconTitles.CHECK_IN}
                                />
                            </ManageResourceVisibilityContainer>

                        </GridContainer>

                        <br/>
                        <br/>
                    </>
                }
                {
                    canAdministrate &&
                    <>
                        <header>
                            <h2>Administration</h2>
                        </header>
                        <GridContainer hasMarginX hasMarginY className={`dashboard`}>
                            {
                                canAdminStudents &&
                                <DashboardTile
                                    {...locations.STUDENT_MANAGEMENT.link}
                                    description={"Search, view, update"}
                                    iconName="Student-Management"
                                />
                            }
                            {
                                canAdminUsers &&
                                <DashboardTile
                                    {...locations.USER_MANAGEMENT.link}
                                    description={"View and manage all users"}
                                    iconName="User-Management"
                                />
                            }
                            {
                                canAdminReports &&
                                <DashboardTile
                                    {...locations.REPORTS.link}
                                    description={"View reports"}
                                    iconName="Reports"
                                />
                            }
                        </GridContainer>
                        <br/>
                        <br/>
                    </>
                }
            </LayoutFullBleed>
        </>
    );
};
